import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Check if user is logged in on mount
        const storedUser = localStorage.getItem('user');
        const storedRole = localStorage.getItem('userRole');
        
        if (storedUser && storedRole && token) {
            try {
                const parsed = JSON.parse(storedUser);
                setUser(parsed);
                setUserRole(storedRole);
            } catch (e) {
                // Corrupted storage — clear it
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                localStorage.removeItem('userRole');
            }
        }
        setLoading(false);
    }, [token]);

    const login = async (credentials, role) => {
        try {
            const response = await authAPI.login(credentials, role);
            const { data } = response;
            
            // API may return just a token or a token + user object
            const user = data.user || { email: credentials.email, role };
            
            // Store token and user info
            localStorage.setItem('token', data.token);
            localStorage.setItem('user', JSON.stringify(user));
            localStorage.setItem('userRole', role);
            
            setToken(data.token);
            setUser(user);
            setUserRole(role);
            
            return { success: true, data };
        } catch (error) {
            const message = error.response?.data?.message || 'Login failed. Please check your credentials.';
            return { success: false, error: message };
        }
    };

    const logout = async () => {
        try {
            await authAPI.logout();
        } catch (error) {
            // Ignore logout errors
        } finally {
            // Clear local storage and state
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('userRole');
            setToken(null);
            setUser(null);
            setUserRole(null);
        }
    };

    const isAuthenticated = () => {
        return !!token && !!user;
    };

    const hasRole = (requiredRole) => {
        if (!userRole) return false;
        if (requiredRole === 'admin') {
            return userRole === 'admin';
        }
        return userRole === requiredRole;
    };

    const value = {
        user,
        userRole,
        token,
        loading,
        login,
        logout,
        isAuthenticated,
        hasRole,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
